import { useState, useEffect, useRef, useCallback } from 'react';
import { PLAYER_COLORS } from '../constants';
import { tFn } from '../i18n';
import { getProfileSettings, updateAllProfileSettings } from '../utils/supabaseActions';
import { DEFAULT_THEME_KEY, normalizeThemeKey } from '../theme';

const DEFAULT_SETTINGS = {
  viewMode: '3d',
  preferredColorIndex: 0,
  showSymbols: false,
  showReactions: true,
  chatSoundsEnabled: false,
  language: 'en',
  themeKey: DEFAULT_THEME_KEY,
};

const ALLOWED_VIEW_MODES = new Set(['2d', '3d', 'fancy3d']);
const ALLOWED_LANGUAGES = new Set(['en', 'pl']);
const STORAGE_KEYS = {
  language: 'kosteczki_lang',
  themeKey: 'kosteczki_theme',
};

const readStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage is best-effort; keep the in-memory state valid even if persistence fails.
  }
};

const sanitizeLanguage = (value) => (
  ALLOWED_LANGUAGES.has(value) ? value : DEFAULT_SETTINGS.language
);

const sanitizeViewMode = (value) => {
  // NOTE: Legacy 'flat' alias was migrated to '2d' via schema.sql one-time migration.
  // Runtime no longer converts flat→2d; all existing profiles have been cleaned up.
  return ALLOWED_VIEW_MODES.has(value) ? value : DEFAULT_SETTINGS.viewMode;
};

const sanitizePreferredColorIndex = (value) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isInteger(parsed) && parsed >= 0 && parsed < PLAYER_COLORS.length) {
    return parsed;
  }
  return DEFAULT_SETTINGS.preferredColorIndex;
};

const sanitizeBoolean = (value, fallback) => {
  if (typeof value === 'boolean') return value;
  if (value === 'true' || value === '1') return true;
  if (value === 'false' || value === '0') return false;
  return fallback;
};

const sanitizeSettings = (settings = {}) => ({
  viewMode: sanitizeViewMode(settings.viewMode),
  preferredColorIndex: sanitizePreferredColorIndex(settings.preferredColorIndex),
  showSymbols: sanitizeBoolean(settings.showSymbols, DEFAULT_SETTINGS.showSymbols),
  showReactions: sanitizeBoolean(settings.showReactions, DEFAULT_SETTINGS.showReactions),
  chatSoundsEnabled: sanitizeBoolean(settings.chatSoundsEnabled, DEFAULT_SETTINGS.chatSoundsEnabled),
  language: sanitizeLanguage(settings.language),
  themeKey: normalizeThemeKey(settings.themeKey || DEFAULT_SETTINGS.themeKey),
});

const readBootSettings = () => {
  const rawLanguage = readStorage(STORAGE_KEYS.language);
  const rawThemeKey = readStorage(STORAGE_KEYS.themeKey);
  const language = sanitizeLanguage(rawLanguage);
  const themeKey = normalizeThemeKey(rawThemeKey || DEFAULT_SETTINGS.themeKey);

  if (rawLanguage !== null && rawLanguage !== language) {
    writeStorage(STORAGE_KEYS.language, language);
  }
  if (rawThemeKey !== null && rawThemeKey !== themeKey) {
    writeStorage(STORAGE_KEYS.themeKey, themeKey);
  }

  return { language, themeKey };
};

/**
 * useSettings — manages user preferences (viewMode, color, symbols, reactions, language, theme).
 *
 * Reads from localStorage pre-login, syncs with Supabase post-login.
 * Debounces DB writes by 500ms.
 *
 * @param {string|null} currentUser — logged-in username, or null
 * @returns {Object} settings state + setters + helpers
 */
export function useSettings(currentUser) {
  const bootSettingsRef = useRef(null);
  if (bootSettingsRef.current === null) {
    bootSettingsRef.current = readBootSettings();
  }

  const [viewMode, setViewModeState] = useState(DEFAULT_SETTINGS.viewMode);
  const [preferredColorIndex, setPreferredColorIndexState] = useState(DEFAULT_SETTINGS.preferredColorIndex);
  const [showSymbols, setShowSymbolsState] = useState(DEFAULT_SETTINGS.showSymbols);
  const [showReactions, setShowReactionsState] = useState(DEFAULT_SETTINGS.showReactions);
  const [chatSoundsEnabled, setChatSoundsEnabledState] = useState(DEFAULT_SETTINGS.chatSoundsEnabled);
  const [language, setLanguage] = useState(() => bootSettingsRef.current.language);
  const [themeKey, setThemeKey] = useState(() => bootSettingsRef.current.themeKey);

  // Track initialisation to avoid saving defaults on mount
  const isInitializingRef = useRef(true);
  const settingsSaveTimeoutRef = useRef(null);

  const setViewMode = useCallback((nextViewMode) => {
    setViewModeState((currentValue) => sanitizeViewMode(typeof nextViewMode === 'function' ? nextViewMode(currentValue) : nextViewMode));
  }, []);

  const setPreferredColorIndex = useCallback((nextColorIndex) => {
    setPreferredColorIndexState((currentValue) => sanitizePreferredColorIndex(typeof nextColorIndex === 'function' ? nextColorIndex(currentValue) : nextColorIndex));
  }, []);

  const setShowSymbols = useCallback((nextValue) => {
    setShowSymbolsState((currentValue) => sanitizeBoolean(typeof nextValue === 'function' ? nextValue(currentValue) : nextValue, DEFAULT_SETTINGS.showSymbols));
  }, []);

  const setShowReactions = useCallback((nextValue) => {
    setShowReactionsState((currentValue) => sanitizeBoolean(typeof nextValue === 'function' ? nextValue(currentValue) : nextValue, DEFAULT_SETTINGS.showReactions));
  }, []);

  const setChatSoundsEnabled = useCallback((nextValue) => {
    setChatSoundsEnabledState((currentValue) => sanitizeBoolean(typeof nextValue === 'function' ? nextValue(currentValue) : nextValue, DEFAULT_SETTINGS.chatSoundsEnabled));
  }, []);

  // Keep localStorage in sync so the login screen always reflects last choice
  const setLanguageAndPersist = useCallback((lang) => {
    const normalizedLanguage = sanitizeLanguage(lang);
    setLanguage(normalizedLanguage);
    writeStorage(STORAGE_KEYS.language, normalizedLanguage);
  }, []);

  const setThemeKeyAndPersist = useCallback((nextThemeKey) => {
    const normalizedThemeKey = normalizeThemeKey(nextThemeKey);
    setThemeKey(normalizedThemeKey);
    writeStorage(STORAGE_KEYS.themeKey, normalizedThemeKey);
  }, []);

  // Translation helper — returns localised string for the current language
  const t = useCallback((key) => tFn(language, key), [language]);

  /**
   * Load all settings from Supabase profile.
   * Called by the auth hook after login / session restore.
   */
  const loadSettingsFromProfile = useCallback(async (username) => {
    const settings = sanitizeSettings(await getProfileSettings(username));
    setViewMode(settings.viewMode);
    setPreferredColorIndex(settings.preferredColorIndex);
    setShowSymbols(settings.showSymbols);
    setShowReactions(settings.showReactions);
    setChatSoundsEnabled(settings.chatSoundsEnabled);
    setLanguageAndPersist(settings.language);
    setThemeKeyAndPersist(settings.themeKey);
  }, [
    setChatSoundsEnabled,
    setLanguageAndPersist,
    setPreferredColorIndex,
    setShowReactions,
    setShowSymbols,
    setThemeKeyAndPersist,
    setViewMode,
  ]);

  /** Mark initialisation as complete — enables DB saves on subsequent changes. */
  const finishInitializing = useCallback(() => {
    isInitializingRef.current = false;
  }, []);

  /** Reset initialising flag for a fresh login. */
  const resetInitializing = useCallback(() => {
    isInitializingRef.current = true;
  }, []);

  // Debounced save: single UPDATE instead of per-field writes
  useEffect(() => {
    if (!currentUser || isInitializingRef.current) return;

    clearTimeout(settingsSaveTimeoutRef.current);
    settingsSaveTimeoutRef.current = setTimeout(() => {
      const allSettings = sanitizeSettings({
        viewMode,
        preferredColorIndex,
        showSymbols,
        showReactions,
        chatSoundsEnabled,
        language,
        themeKey,
      });
      updateAllProfileSettings(currentUser, allSettings).catch(err => {
        console.error('Failed to save settings:', err);
      });
    }, 500);

    return () => clearTimeout(settingsSaveTimeoutRef.current);
  }, [viewMode, preferredColorIndex, showSymbols, showReactions, chatSoundsEnabled, language, themeKey, currentUser]);

  return {
    // State
    viewMode, setViewMode,
    preferredColorIndex, setPreferredColorIndex,
    showSymbols, setShowSymbols,
    showReactions, setShowReactions,
    chatSoundsEnabled, setChatSoundsEnabled,
    language, setLanguageAndPersist,
    themeKey, setThemeKeyAndPersist,
    // Helpers
    t,
    loadSettingsFromProfile,
    finishInitializing,
    resetInitializing,
    isInitializingRef,
  };
}
